import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { DebtAccount } from '../types';
import { Analytics } from '../utils/analytics';

// Übersetzung für Debt Types
const getDebtTypeDisplayName = (type: string): string => {
  const translations: { [key: string]: string } = {
    'LOAN': 'Kredit',
    'CREDIT_CARD': 'Kreditkarte',
    'MORTGAGE': 'Hypothek',
    'PERSONAL_LOAN': 'Privatkredit',
    'STUDENT_LOAN': 'Studienkredit',
    'AUTO_LOAN': 'Autokredit',
    'COLLECTION': 'Inkasso',
    'OTHER': 'Sonstiges'
  };
  return translations[type] || type;
};

// CSS für Timeline-Tooltip
const style = document.createElement('style');
style.textContent = `
  .timeline-tooltip {
    opacity: 0 !important;
  }
  .timeline-point:hover .timeline-tooltip {
    opacity: 1 !important;
  }
`;
if (!document.head.querySelector('style[data-timeline-tooltip]')) {
  style.setAttribute('data-timeline-tooltip', 'true');
  document.head.appendChild(style);
}

const Debts: React.FC = () => {
  const [debts, setDebts] = useState<DebtAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedPaymentHistory, setExpandedPaymentHistory] = useState<Set<string>>(new Set());
  const isMobile = window.innerWidth <= 768;
  const [formData, setFormData] = useState({
    name: '',
    type: 'LOAN',
    status: 'ACTIVE',
    originalAmount: '',
    currentBalance: '',
    interestRate: '',
    creditor: '',
    contractNumber: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    monthlyPayment: '',
    monthlyPaymentType: 'FIXED',
    monthlyPaymentPercent: '',
    nextPaymentDate: '',
    description: '',
    includeInDailyBudget: true
  });

  useEffect(() => {
    fetchDebts();
  }, []);

  const calculatePayoffDate = (debt: DebtAccount): string => {
    const currentBalance = parseFloat(debt.currentBalance.toString());
    const interestRate = debt.interestRate ? parseFloat(debt.interestRate.toString()) : 0;
    const monthlyInterestRate = interestRate / 100 / 12;

    if (currentBalance <= 0) return 'Abbezahlt';

    // Für feste monatliche Zahlung
    if (debt.monthlyPaymentType === 'FIXED' && debt.monthlyPayment) {
      const monthlyPayment = parseFloat(debt.monthlyPayment.toString());
      if (monthlyPayment <= 0) return '-';

      let balance = currentBalance;
      let months = 0;
      const maxMonths = 1200; // Maximal 100 Jahre

      while (balance > 0 && months < maxMonths) {
        const interest = balance * monthlyInterestRate;
        const principal = monthlyPayment - interest;
        
        if (principal <= 0) return 'Zinsen > Rate';
        
        balance -= principal;
        months++;
      }

      if (months >= maxMonths) return '> 100 Jahre';

      const payoffDate = new Date();
      payoffDate.setMonth(payoffDate.getMonth() + months);
      return payoffDate.toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' });
    }

    // Für prozentuale Zahlung
    if (debt.monthlyPaymentType === 'PERCENTAGE' && debt.monthlyPaymentPercent) {
      const percent = parseFloat(debt.monthlyPaymentPercent.toString());
      if (percent <= 0) return '-';

      let balance = currentBalance;
      let months = 0;
      const maxMonths = 1200;

      while (balance > 0.01 && months < maxMonths) {
        const interest = balance * monthlyInterestRate;
        const percentPayment = balance * (percent / 100);
        const totalPayment = percentPayment + interest;
        
        balance -= (totalPayment - interest);
        months++;

        // Sicherheitscheck: wenn die Schuld wächst statt zu schrumpfen
        if (percentPayment <= interest) return 'Zinsen ≥ Rate';
      }

      if (months >= maxMonths) return '> 100 Jahre';

      const payoffDate = new Date();
      payoffDate.setMonth(payoffDate.getMonth() + months);
      return payoffDate.toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' });
    }

    return '-';
  };

  const fetchDebts = async () => {
    try {
      const response = await api.get('/debts');
      const fetchedDebts = response.data.data.debtAccounts;
      // Sortiere: Aktive zuerst, dann abbezahlte
      fetchedDebts.sort((a: DebtAccount, b: DebtAccount) => {
        if (a.status === 'PAID_OFF' && b.status !== 'PAID_OFF') return 1;
        if (a.status !== 'PAID_OFF' && b.status === 'PAID_OFF') return -1;
        return 0;
      });
      setDebts(fetchedDebts);
    } catch (error) {
      console.error('Fehler beim Laden der Schulden:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/debts/${editingId}`, formData);
      } else {
        await api.post('/debts', formData);
        Analytics.debtCreated(formData.type);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({
        name: '',
        type: 'LOAN',
        status: 'ACTIVE',
        originalAmount: '',
        currentBalance: '',
        interestRate: '',
        creditor: '',
        contractNumber: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        monthlyPayment: '',
        monthlyPaymentType: 'FIXED',
        monthlyPaymentPercent: '',
        nextPaymentDate: '',
        description: '',
        includeInDailyBudget: true
      });
      fetchDebts();
    } catch (error) {
      console.error('Fehler beim Speichern der Schuld:', error);
    }
  };

  const handleEdit = (debt: DebtAccount) => {
    setEditingId(debt.id);
    setFormData({
      name: debt.name,
      type: debt.type,
      status: debt.status,
      originalAmount: debt.originalAmount.toString(),
      currentBalance: debt.currentBalance.toString(),
      interestRate: debt.interestRate?.toString() || '',
      creditor: debt.creditor,
      contractNumber: debt.contractNumber || '',
      startDate: new Date(debt.startDate).toISOString().split('T')[0],
      endDate: debt.endDate ? new Date(debt.endDate).toISOString().split('T')[0] : '',
      monthlyPayment: debt.monthlyPayment?.toString() || '',
      monthlyPaymentType: (debt as any).monthlyPaymentType || 'FIXED',
      monthlyPaymentPercent: (debt as any).monthlyPaymentPercent?.toString() || '',
      nextPaymentDate: debt.nextPaymentDate ? new Date(debt.nextPaymentDate).toISOString().split('T')[0] : '',
      description: debt.description || '',
      includeInDailyBudget: (debt as any).includeInDailyBudget !== undefined ? (debt as any).includeInDailyBudget : true
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Schuldenkonto wirklich löschen?')) {
      try {
        await api.delete(`/debts/${id}`);
        Analytics.debtDeleted();
        fetchDebts();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  const togglePaymentHistory = (debtId: string) => {
    const newExpanded = new Set(expandedPaymentHistory);
    if (newExpanded.has(debtId)) {
      newExpanded.delete(debtId);
    } else {
      newExpanded.add(debtId);
    }
    setExpandedPaymentHistory(newExpanded);
  };

  const handleReactivateDebt = async (debt: DebtAccount) => {
    if (window.confirm(`Schuld "${debt.name}" wieder aktivieren? Der Status wird auf AKTIV gesetzt.`)) {
      try {
        // Berechne nächstes Zahlungsdatum (heute + 1 Monat)
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        
        await api.put(`/debts/${debt.id}`, {
          ...debt,
          status: 'ACTIVE',
          isActive: true,
          nextPaymentDate: nextMonth.toISOString().split('T')[0]
        });
        
        fetchDebts();
      } catch (error) {
        console.error('Fehler beim Reaktivieren:', error);
        alert('Fehler beim Reaktivieren der Schuld');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: any = {
      ACTIVE: 'badge-warning',
      PAID_OFF: 'badge-success',
      DEFAULTED: 'badge-danger',
      SETTLED: 'badge-success',
      IN_COLLECTION: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const getStatusText = (status: string) => {
    const texts: any = {
      ACTIVE: 'Aktiv',
      PAID_OFF: 'Abbezahlt',
      DEFAULTED: 'In Verzug',
      SETTLED: 'Beglichen',
      IN_COLLECTION: 'Im Inkasso'
    };
    return texts[status] || status;
  };

  // Timeline-Daten mit useMemo cachen um Re-Renders zu vermeiden
  const timelineData = useMemo(() => {
    const today = new Date();
    const threeYearsFromNow = new Date(today);
    threeYearsFromNow.setFullYear(today.getFullYear() + 3);

    const activeDebts = debts.filter(d => d.status === 'ACTIVE').map(debt => ({
      debt,
      payoffDate: calculatePayoffDate(debt),
      currentBalance: parseFloat(debt.currentBalance.toString())
    })).filter(d => {
      // Nur Schulden mit berechenbarem Datum
      const date = d.payoffDate;
      return date !== '-' && date !== 'Zinsen > Rate' && date !== 'Zinsen ≥ Rate' && date !== '> 100 Jahre' && date !== 'Abbezahlt';
    });

    const shortTermDebts = activeDebts.filter(d => {
      const debtDate = new Date(d.payoffDate.split('.').reverse().join('-'));
      return debtDate <= threeYearsFromNow;
    }).sort((a, b) => {
      const dateA = new Date(a.payoffDate.split('.').reverse().join('-'));
      const dateB = new Date(b.payoffDate.split('.').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });

    const longTermDebts = activeDebts.filter(d => {
      const debtDate = new Date(d.payoffDate.split('.').reverse().join('-'));
      return debtDate > threeYearsFromNow;
    });

    const lastPayoff = shortTermDebts.length > 0 
      ? new Date(shortTermDebts[shortTermDebts.length - 1].payoffDate.split('.').reverse().join('-'))
      : threeYearsFromNow;
    const totalMonths = Math.max(1, Math.ceil((lastPayoff.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30)));

    return {
      today,
      activeDebts,
      shortTermDebts,
      longTermDebts,
      totalMonths
    };
  }, [debts]);

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        marginBottom: '20px' 
      }}>
        <h1 style={{ 
          minWidth: isMobile ? '100%' : 'auto',
          marginBottom: isMobile ? '10px' : '0'
        }}>
          Schulden & Kredite
        </h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
          style={{
            width: isMobile ? '100%' : 'auto',
            padding: '8px 16px'
          }}
        >
          {showForm ? 'Abbrechen' : '+ Neue Schuld'}
        </button>
      </div>

      {/* Tilgungs-Timeline */}
      {timelineData.activeDebts.length > 0 && (
          <div className="card" style={{ 
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
            borderRadius: '16px',
            boxShadow: '0 8px 20px rgba(15, 32, 39, 0.4)',
            color: 'white',
            padding: '30px'
          }}>
            <h2 style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '25px',
              color: 'white' 
            }}>
              <span style={{ fontSize: '28px' }}>🎯</span> Weg zur Schuldenfreiheit (nächste 3 Jahre)
            </h2>
            
            {timelineData.shortTermDebts.length > 0 ? (
              <>
                {/* Timeline Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: '15px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: 0.9
                }}>
                  <div>📍 Heute</div>
                  <div>✨ {timelineData.shortTermDebts[timelineData.shortTermDebts.length - 1].payoffDate}</div>
                </div>

                {/* Timeline Bar */}
                <div style={{ 
                  position: 'relative',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  height: '12px',
                  marginBottom: '60px',
                  marginTop: '40px'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '100%',
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.5) 100%)',
                    borderRadius: '10px'
                  }} />
                  
                  {/* Meilenstein-Punkte */}
                  {timelineData.shortTermDebts.map((item, idx) => {
                    const debtDate = new Date(item.payoffDate.split('.').reverse().join('-'));
                    const monthsFromNow = (debtDate.getTime() - timelineData.today.getTime()) / (1000 * 60 * 60 * 24 * 30);
                    let position = (monthsFromNow / timelineData.totalMonths) * 100;
                    
                    // Bei mehreren Schulden am gleichen Datum: leichter Versatz
                    const sameMonthDebts = timelineData.shortTermDebts.filter(d => {
                      const dDate = new Date(d.payoffDate.split('.').reverse().join('-'));
                      return Math.abs((dDate.getTime() - debtDate.getTime()) / (1000 * 60 * 60 * 24)) < 15; // innerhalb 15 Tage
                    });
                    
                    if (sameMonthDebts.length > 1) {
                      const offsetIndex = sameMonthDebts.findIndex(d => d.debt.id === item.debt.id);
                      const spread = Math.min(sameMonthDebts.length * 2, 8); // max 8% spread
                      const individualOffset = ((offsetIndex - (sameMonthDebts.length - 1) / 2) / (sameMonthDebts.length - 1)) * spread;
                      position += individualOffset;
                    }
                    
                    position = Math.min(Math.max(position, 2), 98);
                    
                    return (
                      <div 
                        key={item.debt.id}
                        className="timeline-point"
                        style={{
                          position: 'absolute',
                          left: `${position}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 100 + idx
                        }}
                      >
                        {/* Punkt */}
                        <div style={{
                          width: '20px',
                          height: '20px',
                          background: 'white',
                          borderRadius: '50%',
                          border: '4px solid rgba(102, 126, 234, 1)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.3)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                        }}>
                          {/* Tooltip */}
                          <div className="timeline-tooltip" style={{
                            position: 'absolute',
                            bottom: '30px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.95)',
                            color: 'white',
                            padding: '14px 18px',
                            borderRadius: '10px',
                            whiteSpace: 'nowrap',
                            fontSize: '13px',
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.3s ease',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
                            zIndex: 1000,
                            minWidth: '200px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '15px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '6px' }}>
                              {item.debt.name}
                            </div>
                            <div style={{ opacity: 0.95, marginBottom: '5px', display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                              <span>💰 Restbetrag:</span>
                              <strong>{item.currentBalance.toFixed(2)} €</strong>
                            </div>
                            <div style={{ opacity: 0.95, marginBottom: '5px', display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                              <span>📅 Abbezahlt:</span>
                              <strong>{item.payoffDate}</strong>
                            </div>
                            <div style={{ opacity: 0.95, marginBottom: '5px', display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                              <span>💸 Monatlich:</span>
                              <strong>{item.debt.monthlyPaymentType === 'FIXED' 
                                ? `${parseFloat(item.debt.monthlyPayment?.toString() || '0').toFixed(2)} €`
                                : `${item.debt.monthlyPaymentPercent}% vom Saldo`
                              }</strong>
                            </div>
                            {item.debt.interestRate && parseFloat(item.debt.interestRate.toString()) > 0 && (
                              <div style={{ opacity: 0.95, display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                                <span>📈 Zinsen:</span>
                                <strong>{parseFloat(item.debt.interestRate.toString()).toFixed(2)}%</strong>
                              </div>
                            )}
                            {/* Pfeil nach unten */}
                            <div style={{
                              position: 'absolute',
                              bottom: '-6px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 0,
                              height: 0,
                              borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent',
                              borderTop: '6px solid rgba(0,0,0,0.95)'
                            }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                opacity: 0.8
              }}>
                Keine Schulden in den nächsten 3 Jahren abbezahlt
              </div>
            )}

            {/* Langfristige Schulden */}
            {timelineData.longTermDebts.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', opacity: 0.9 }}>
                  ⏰ Langfristige Schulden (&gt; 3 Jahre):
                </div>
                {timelineData.longTermDebts.map(item => (
                  <div key={item.debt.id} style={{ 
                    marginBottom: '5px',
                    paddingLeft: '10px',
                    opacity: 0.85
                  }}>
                    • {item.debt.name}: {item.payoffDate}
                  </div>
                ))}
              </div>
            )}

            {debts.filter(d => d.status === 'ACTIVE').length > timelineData.activeDebts.length && (
              <div style={{
                marginTop: '15px',
                padding: '12px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                fontSize: '12px',
                opacity: 0.8
              }}>
                ⚠️ {debts.filter(d => d.status === 'ACTIVE').length - timelineData.activeDebts.length} Schuld(en) ohne berechenbare Tilgung (keine Rate oder Zinsen zu hoch)
              </div>
            )}
          </div>
        )}

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Schuld bearbeiten' : 'Neue Schuld/Kredit erstellen'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: '15px' 
            }}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Typ *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="LOAN">Kredit</option>
                  <option value="COLLECTION">Inkasso</option>
                  <option value="MORTGAGE">Hypothek</option>
                  <option value="STUDENT_LOAN">Studienkredit</option>
                  <option value="CREDIT_CARD">Kreditkartenschuld</option>
                  <option value="OTHER">Sonstiges</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ursprünglicher Betrag *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.originalAmount}
                  onChange={(e) => setFormData({ ...formData, originalAmount: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Aktueller Saldo *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.currentBalance}
                  onChange={(e) => setFormData({ ...formData, currentBalance: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Zinssatz (%) </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.interestRate}
                  onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Gläubiger *</label>
                <input
                  type="text"
                  value={formData.creditor}
                  onChange={(e) => setFormData({ ...formData, creditor: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Vertragsnummer</label>
                <input
                  type="text"
                  value={formData.contractNumber}
                  onChange={(e) => setFormData({ ...formData, contractNumber: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Startdatum *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Zahlungsart</label>
                <select
                  value={formData.monthlyPaymentType}
                  onChange={(e) => setFormData({ ...formData, monthlyPaymentType: e.target.value })}
                >
                  <option value="FIXED">Fester Betrag</option>
                  <option value="PERCENTAGE">Prozentsatz vom Saldo</option>
                </select>
              </div>
              {formData.monthlyPaymentType === 'FIXED' ? (
                <div className="form-group">
                  <label>Monatliche Zahlung (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthlyPayment}
                    onChange={(e) => setFormData({ ...formData, monthlyPayment: e.target.value })}
                    placeholder="z.B. 50.00"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Monatlicher Prozentsatz (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthlyPaymentPercent}
                    onChange={(e) => setFormData({ ...formData, monthlyPaymentPercent: e.target.value })}
                    placeholder="z.B. 5.0 für 5%"
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    Wird vom aktuellen Saldo berechnet: {formData.currentBalance && formData.monthlyPaymentPercent 
                      ? `${(parseFloat(formData.currentBalance) * parseFloat(formData.monthlyPaymentPercent) / 100).toFixed(2)} €`
                      : '0.00 €'} + Zinsen
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Nächste Zahlung</label>
                <input
                  type="date"
                  value={formData.nextPaymentDate}
                  onChange={(e) => setFormData({ ...formData, nextPaymentDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <input
                    type="checkbox"
                    checked={formData.includeInDailyBudget}
                    onChange={(e) => setFormData({ ...formData, includeInDailyBudget: e.target.checked })}
                    style={{ 
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px',
                      accentColor: '#667eea'
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', color: '#333' }}>In Tagesbudget einrechnen</div>
                    <small style={{ color: '#6c757d', display: 'block', marginTop: '2px' }}>
                      Diese Schuld bei der Berechnung des verfügbaren Tagesbudgets berücksichtigen
                    </small>
                  </div>
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-success"
              style={{
                width: isMobile ? '100%' : 'auto',
                padding: '12px 24px',
                marginTop: '10px'
              }}
            >
              {editingId ? 'Änderungen speichern' : 'Schuld erstellen'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        {debts.length === 0 ? (
          <p>Keine Schulden vorhanden.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Gläubiger</th>
                <th>Original</th>
                <th>Aktuell</th>
                <th>Zinssatz</th>
                <th>Monatl. Rate</th>
                <th>Voraussichtlich abbezahlt</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {debts.map((debt) => {
                const isPaidOff = debt.status === 'PAID_OFF';
                const currentBalance = parseFloat(debt.currentBalance.toString());
                const isExpanded = expandedPaymentHistory.has(debt.id);
                
                return (
                  <React.Fragment key={debt.id}>
                    <tr style={{ 
                      opacity: isPaidOff ? 0.6 : 1,
                      background: isPaidOff ? '#f8f9fa' : 'transparent'
                    }}>
                      <td data-label="Name" style={{ fontWeight: isPaidOff ? 'normal' : 'bold' }}>
                        {debt.name}
                      </td>
                      <td data-label="Typ">{getDebtTypeDisplayName(debt.type)}</td>
                      <td data-label="Gläubiger">{debt.creditor}</td>
                      <td data-label="Original">{parseFloat(debt.originalAmount.toString()).toFixed(2)} €</td>
                      <td data-label="Aktuell" style={{ 
                        color: currentBalance === 0 ? '#28a745' : '#dc3545', 
                        fontWeight: 'bold' 
                      }}>
                        {currentBalance.toFixed(2)} €
                      </td>
                      <td data-label="Zinssatz">{debt.interestRate ? `${debt.interestRate}%` : '-'}</td>
                      <td data-label="Monatl. Rate">
                        {debt.monthlyPaymentType === 'PERCENTAGE' && debt.monthlyPaymentPercent 
                          ? `~${debt.monthlyPaymentPercent}% + Zinsen` 
                          : debt.monthlyPayment 
                            ? `${parseFloat(debt.monthlyPayment.toString()).toFixed(2)} €` 
                            : '-'}
                      </td>
                      <td data-label="Voraussichtlich abbezahlt" style={{ fontStyle: 'italic', color: '#6c757d' }}>
                        {calculatePayoffDate(debt)}
                      </td>
                      <td data-label="Status">
                        <span className={`badge ${getStatusBadge(debt.status)}`}>
                          {getStatusText(debt.status)}
                        </span>
                      </td>
                      <td data-label="Aktionen">
                        <div style={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: isMobile ? '8px' : '5px'
                        }}>
                          <button
                            className="btn btn-info"
                            style={{ 
                              fontSize: '12px', 
                              padding: '5px 10px',
                              width: isMobile ? '100%' : 'auto',
                              background: '#17a2b8',
                              border: 'none',
                              color: 'white'
                            }}
                            onClick={() => togglePaymentHistory(debt.id)}
                          >
                            {isExpanded ? '▲ Verlauf' : '▼ Verlauf'}
                          </button>
                          {isPaidOff && (
                            <button
                              className="btn btn-warning"
                              style={{ 
                                fontSize: '12px', 
                                padding: '5px 10px',
                                width: isMobile ? '100%' : 'auto',
                                background: '#ffc107',
                                border: 'none',
                                color: '#000'
                              }}
                              onClick={() => handleReactivateDebt(debt)}
                            >
                              🔄 Reaktivieren
                            </button>
                          )}
                          <button
                            className="btn btn-primary"
                            style={{ 
                              fontSize: '12px', 
                              padding: '5px 10px',
                              width: isMobile ? '100%' : 'auto'
                            }}
                            onClick={() => handleEdit(debt)}
                          >
                            Bearbeiten
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ 
                              fontSize: '12px', 
                              padding: '5px 10px',
                              width: isMobile ? '100%' : 'auto'
                            }}
                            onClick={() => handleDelete(debt.id)}
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Zahlungsverlauf Zeile */}
                    {isExpanded && (
                      <tr style={{ background: '#f8f9fa' }}>
                        <td colSpan={10} style={{ padding: '15px' }}>
                          <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
                            📅 Zahlungsverlauf
                          </div>
                          {debt.payments && debt.payments.length > 0 ? (
                            <table style={{ width: '100%', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Datum</th>
                                  <th style={{ padding: '8px', textAlign: 'right' }}>Betrag</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {debt.payments
                                  .sort((a: any, b: any) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())
                                  .map((payment: any, idx: number) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                                      <td style={{ padding: '8px' }}>
                                        {new Date(payment.paymentDate).toLocaleDateString('de-DE')}
                                      </td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: payment.isPaid ? '#28a745' : '#6c757d' }}>
                                        {parseFloat(payment.amount).toFixed(2)} €
                                      </td>
                                      <td style={{ padding: '8px' }}>
                                        <span style={{
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          background: payment.isPaid ? '#d4edda' : '#fff3cd',
                                          color: payment.isPaid ? '#155724' : '#856404'
                                        }}>
                                          {payment.isPaid ? '✓ Bezahlt' : '⏳ Ausstehend'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ color: '#6c757d', fontStyle: 'italic', padding: '10px' }}>
                              Noch keine Zahlungen erfasst
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Debts;
